import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import ipconfig from "../../Core/ipconfig";
import {
  Box,
  MenuItem,
  Select,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  useTheme,
  Paper,
  Divider,
} from "@mui/material";
import SearchBar from "../../Shared/Widgets/SearchBar";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";

export default function Logs() {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [allCompanyData, setAllCompanyData] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  // Pagination states for companies
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);

  const [logs, setLogs] = useState<any[]>([]);
  const [live, setLive] = useState(true);
  const [level, setLevel] = useState<"all" | "info" | "warn" | "error">("all");
  const [logSearch, setLogSearch] = useState("");
  const [range, setRange] = useState<
    "15m" | "1h" | "6h" | "12h" | "1d" | "7d" | "15d" | "30d"
  >("1h");

  const logContainerRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchValue]);

  const fetchCompanies = async (isFirstPage = false) => {
    setLoading(true);
    try {
      const activePage = isFirstPage ? 1 : page;
      const res = await axios.get(
        `${ipconfig}/companies/fetch?search=${encodeURIComponent(debouncedSearchValue)}&page=${activePage}&limit=15`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const companies = res.data.data || [];
      const pagination = res.data.pagination || { totalItems: companies.length, totalPages: 1, currentPage: 1 };

      if (activePage === 1) {
        const allCompaniesOption = {
          _id: null,
          tally_companyInfo: { Name: "All Companies" },
        };
        const list = [allCompaniesOption, ...companies];
        setAllCompanyData(list);
        setSelectedCompany(allCompaniesOption);
      } else {
        setAllCompanyData((prev) => {
          const existingIds = new Set(prev.map((c: any) => c._id));
          const filteredNew = companies.filter((c: any) => !existingIds.has(c._id));
          return [...prev, ...filteredNew];
        });
      }
      setTotalPages(pagination.totalPages);
      setTotalItems(pagination.totalItems);
    } catch (err) {
      console.error("Failed to fetch companies", err);
    } finally {
      setLoading(false);
    }
  };

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
    fetchCompanies(true);
  }, [debouncedSearchValue]);

  // Fetch subsequent pages
  useEffect(() => {
    if (page > 1) {
      fetchCompanies(false);
    }
  }, [page]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 30) {
      if (!loading && page < totalPages) {
        setPage((prev) => prev + 1);
      }
    }
  };

  const [autoScroll, setAutoScroll] = useState(true);
  const autoScrollRef = useRef(true);

  useEffect(() => {
    autoScrollRef.current = autoScroll;
  }, [autoScroll]);

  const scrollToBottom = () => {
    setAutoScroll(true);
    if (logContainerRef.current) {
      logContainerRef.current.scrollTo({
        top: logContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const handleLogScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 30;
    if (isAtBottom) {
      setAutoScroll(true);
    } else {
      setAutoScroll(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await axios.get(
        `${ipconfig}/admin/logs`,
        {
          params: {
            Code: selectedCompany?._id || undefined,
            level,
            range,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setLogs(res.data.logs || []);
      setTimeout(() => {
        if (logContainerRef.current) {
          logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
      }, 80);
      setAutoScroll(true);
    } catch (err) {
      console.error("Failed to fetch logs", err);
    }
  };

  const lastLogKeyRef = useRef<string | null>(null);

  const startLiveLogs = () => {
    stopLiveLogs();
    setLogs([]);

    const token = localStorage.getItem("token");
    const es = new EventSource(
      `${ipconfig}/admin/logs/live?level=${level}&token=${token}${selectedCompany?._id ? `&Code=${selectedCompany._id}` : ""}`
    );

    eventSourceRef.current = es;

    es.onopen = () => {
      console.log("🟢 Live logs connected");
    };

    es.onmessage = (event) => {
      const data = JSON.parse(event.data);
      const messageStr = typeof data.message === "object" && data.message !== null
        ? (data.message.msg || data.message.message || JSON.stringify(data.message))
        : (data.message || "");

      const key = `${data.timestamp}-${messageStr}`;
      if (lastLogKeyRef.current === key) return;
      lastLogKeyRef.current = key;

      setLogs((prev) => [...prev, data]);

      setTimeout(() => {
        if (logContainerRef.current && autoScrollRef.current) {
          logContainerRef.current.scrollTo({
            top: logContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 50);
    };

    es.onerror = (e) => {
      console.error("🔴 SSE error", e);
      stopLiveLogs();
    };
  };

  const stopLiveLogs = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  };

  useEffect(() => {
    if (live) {
      startLiveLogs();
    } else {
      stopLiveLogs();
      fetchLogs();
    }
    return () => stopLiveLogs();
  }, [selectedCompany, live, level, range]);

  const levelColor = (lvl: string) => {
    switch (lvl) {
      case "error":
        return "#ef4444";
      case "warn":
        return "#f59e0b";
      case "info":
      default:
        return "#22c55e";
    }
  };

  const formatLogMessage = (message: any): string => {
    if (message === null || message === undefined) return "";
    if (typeof message === "object") {
      return message.msg || message.message || JSON.stringify(message);
    }
    return String(message);
  };

  const formatTimestamp = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const filteredLogs = React.useMemo(() => {
    if (!logSearch) return logs;
    const q = logSearch.toLowerCase();

    return logs.filter((log) => {
      const rawIsoTime = log.timestamp ? new Date(log.timestamp).toISOString().toLowerCase() : "";
      const formattedTime = log.timestamp ? formatTimestamp(log.timestamp).toLowerCase() : "";
      const messageStr = formatLogMessage(log.message);

      return [
        rawIsoTime,
        formattedTime,
        messageStr,
        log.path,
        log.method,
        log.ip,
        log.requestId,
        log.level,
        log.status,
        log.service,
        log.duration,
        log.CLPCode,
        log.syncRunId,
        log.userAgent
      ]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q));
    });
  }, [logs, logSearch]);

  return (
    <Box sx={{ display: "flex", width: "100%", height: "100%", gap: "10px", p: 1 }}>
      {/* Left Panel: Search Bar + Companies List */}
      <Box sx={{ width: "320px", display: "flex", flexDirection: "column", gap: "12px", flexShrink: 0 }}>
        <Box sx={{ width: "100%" }}>
          <SearchBar onSearch={setSearchValue} />
        </Box>

        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ padding: "12px 16px", borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ color: "text.primary", fontSize: "15px", fontWeight: 700 }}>
              Companies ({totalItems})
            </Typography>
          </Box>

          <Box
            id="company-list-container"
            onScroll={handleScroll}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              padding: "8px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              "&::-webkit-scrollbar": {
                width: "5px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "divider",
                borderRadius: "4px",
              },
            }}
          >
            {loading && page === 1 ? (
              <Spinner height="100%" />
            ) : allCompanyData.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography sx={{ color: "text.secondary", fontSize: "13px" }}>No companies found</Typography>
              </Box>
            ) : (
              <>
                {allCompanyData.map((item, index) => {
                  const isSelected = selectedCompany?._id === item._id;
                  return (
                    <Box
                      key={item._id || index}
                      onClick={() => {
                        setSelectedCompany(item);
                      }}
                      sx={{
                        padding: "12px 16px",
                        cursor: "pointer",
                        backgroundColor: isSelected
                          ? theme.palette.mode === "light" ? "#f0f7ff" : "#1e3a8a33"
                          : "background.paper",
                        border: "1px solid",
                        borderColor: isSelected ? "primary.main" : "divider",
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b80",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "text.primary" }}>
                        {item?.tally_companyInfo?.Name || "Unnamed Company"}
                      </Typography>
                    </Box>
                  );
                })}

                {loading && page > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
                    <Spinner height="30px" />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Right Panel: Console Log Dashboard */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden", gap: "16px", position: "relative" }}>
        {/* Controls row */}
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
          {/* Live vs History Toggle */}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={live ? "live" : "history"}
            onChange={(_, value) => {
              if (value) setLive(value === "live");
            }}
          >
            <ToggleButton
              value="live"
              sx={{
                fontWeight: 700,
                px: 2,
                color: "#22c55e",
                "&.Mui-selected": {
                  backgroundColor: "#22c55e1a",
                  color: "#22c55e",
                }
              }}
            >
              ● LIVE
            </ToggleButton>
            <ToggleButton
              value="history"
              sx={{
                fontWeight: 700,
                px: 2,
                color: "#eab308",
                "&.Mui-selected": {
                  backgroundColor: "#eab3081a",
                  color: "#eab308",
                }
              }}
            >
              ■ HISTORY
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Level Filter */}
          <Select
            size="small"
            value={level}
            onChange={(e) => setLevel(e.target.value as any)}
            sx={{ minWidth: "90px" }}
          >
            <MenuItem value="all">All levels</MenuItem>
            <MenuItem value="info">Info</MenuItem>
            <MenuItem value="warn">Warn</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>

          {/* Time range selector */}
          <Select
            size="small"
            value={range}
            onChange={(e) => setRange(e.target.value as any)}
            disabled={live}
            sx={{ minWidth: "120px" }}
          >
            <MenuItem value="15m">Last 15 min</MenuItem>
            <MenuItem value="1h">Last 1 hour</MenuItem>
            <MenuItem value="6h">Last 6 hours</MenuItem>
            <MenuItem value="12h">Last 12 hours</MenuItem>
            <MenuItem value="1d">Last 1 day</MenuItem>
            <MenuItem value="7d">Last 7 days</MenuItem>
            <MenuItem value="15d">Last 15 days</MenuItem>
            <MenuItem value="30d">Last 30 days</MenuItem>
          </Select>

          {/* Current selected company header label */}
          <Typography sx={{ fontWeight: 700, ml: 1, color: "text.primary" }}>
            {selectedCompany?._id ? selectedCompany.tally_companyInfo?.Name : "All Companies"}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          {/* Search box */}
          <TextField
            size="small"
            placeholder="Search within logs..."
            value={logSearch}
            onChange={(e) => setLogSearch(e.target.value)}
            sx={{ width: "280px" }}
          />
        </Box>

        {/* Dynamic Log Console Viewport */}
        <Box
          ref={logContainerRef}
          onScroll={handleLogScroll}
          sx={{
            flexGrow: 1,
            backgroundColor: "#0d0e12",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            p: 2,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            fontFamily: "Fira Code, Consolas, Monaco, monospace",
            fontSize: "12.5px",
            "&::-webkit-scrollbar": {
              width: "5px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255,255,255,0.15)",
              borderRadius: "4px",
            },
          }}
        >
          {filteredLogs.length > 0 ? (
            filteredLogs.map((log, index) => (
              <Box
                key={index}
                sx={{
                  borderLeft: `3px solid ${levelColor(log.level)}`,
                  backgroundColor: "rgba(255,255,255,0.02)",
                  p: "8px 12px",
                  borderRadius: "0 6px 6px 0",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.04)"
                  }
                }}
              >
                {/* Main line */}
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    fontWeight: 600,
                    color: levelColor(log.level),
                    lineHeight: 1.4,
                  }}
                >
                  [{log?.CLPCode || log?.payload?.companyName || log?.companyId || "ALL"}] [{formatTimestamp(log.timestamp)}] [{log.level.toUpperCase()}] {formatLogMessage(log.message)}
                </Typography>

                {/* Metadata row */}
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "11px",
                    color: "rgba(255,255,255,0.4)",
                    mt: "4px",
                    ml: "4px"
                  }}
                >
                  {log.method} {log.path} • {log.durationMs || log.duration || 0}ms • {log.ip || "unknown-ip"}
                </Typography>

                {/* Details Expander */}
                <details style={{ marginTop: "6px" }}>
                  <summary style={{ cursor: "pointer", color: "rgba(255,255,255,0.3)", fontSize: "11px", outline: "none", userSelect: "none" }}>
                    View Raw JSON payload
                  </summary>
                  <Box
                    component="pre"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      backgroundColor: "rgba(0,0,0,0.3)",
                      p: 1.5,
                      borderRadius: "6px",
                      fontSize: "11px",
                      overflowX: "auto",
                      mt: "8px",
                      fontFamily: "inherit",
                      border: "1px solid rgba(255,255,255,0.05)"
                    }}
                  >
                    {JSON.stringify(log, null, 2)}
                  </Box>
                </details>
              </Box>
            ))
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "rgba(255,255,255,0.3)" }}>
              No log messages matching selected criteria.
            </Box>
          )}
        </Box>

        {/* Floating Scroll to Bottom Button */}
        {!autoScroll && logs.length > 0 && (
          <Box
            onClick={scrollToBottom}
            sx={{
              position: "absolute",
              bottom: "32px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "primary.main",
              color: "#ffffff",
              px: 2,
              py: 1,
              borderRadius: "20px",
              cursor: "pointer",
              boxShadow: "0px 6px 20px rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "11.5px",
              fontWeight: 700,
              zIndex: 100,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "primary.dark",
                transform: "translateX(-50%) scale(1.03)",
              },
              "&:active": {
                transform: "translateX(-50%) scale(0.97)",
              }
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <polyline points="19 12 12 19 5 12"/>
            </svg>
            New Logs Arrived • Scroll to Bottom
          </Box>
        )}
      </Box>
    </Box>
  );
}
